import type { HoComponents } from '@pos/api-client';
import { hoApi } from '../api';

export type OnlineOrderItem = HoComponents['schemas']['OnlineOrderListItem'];
export type OnlineOrderDetail = HoComponents['schemas']['OnlineOrderDetail'];
export type OnlineOrderLine = HoComponents['schemas']['OnlineOrderLineDto'];

/** The server checks these again (FR-ONL-05): reading orders, and revealing the customer's contact (audited). */
export const ORDERS_READ = 'ho.online.orders.read';
export const ORDERS_REVEAL = 'ho.online.orders.manage';

/** online.online_order's statuses, in the order an order goes through them. */
export const ORDER_STATUSES = [
  'received',
  'awaiting_payment',
  'relayed',
  'accepted',
  'preparing',
  'ready',
  'out_for_delivery',
  'delivered',
  'rejected',
  'cancelled',
] as const;
export type OrderStatus = (typeof ORDER_STATUSES)[number];

export const PAYMENT_METHODS = ['cod', 'pay_at_pickup', 'online'] as const;
export type PaymentMethod = (typeof PAYMENT_METHODS)[number];

/** StatusTag colours: in progress blue, done green, ended without it gray, waiting for the store or payment purple. */
export const STATUS_TAG = {
  received: 'pending',
  awaiting_payment: 'awaiting',
  relayed: 'awaiting',
  accepted: 'in_progress',
  preparing: 'in_progress',
  ready: 'in_progress',
  out_for_delivery: 'in_progress',
  delivered: 'active',
  rejected: 'failed',
  cancelled: 'cancelled',
} as const satisfies Record<OrderStatus, string>;

export interface OrderFilters {
  search: string;
  status: string;
  storeId: string;
  salesChannelId: string;
  paymentMethod: string;
  /** "from..to", ISO dates. */
  received: string;
}

export const ordersKey = (filters: OrderFilters, page: number, pageSize: number) =>
  ['ho', 'online-orders', filters, page, pageSize] as const;
export const orderKey = (id: string) => ['ho', 'online-order', id] as const;

export async function listOrders(filters: OrderFilters, page: number, pageSize: number) {
  const [from = '', to = ''] = filters.received.split('..');
  const { data, error } = await hoApi.GET('/api/v1/onlineordering/orders', {
    params: {
      query: {
        search: filters.search || undefined,
        status: filters.status || undefined,
        storeId: filters.storeId || undefined,
        salesChannelId: filters.salesChannelId || undefined,
        paymentMethod: filters.paymentMethod || undefined,
        from: from || undefined,
        to: to || undefined,
        page,
        pageSize,
      },
    },
  });
  if (!data) throw error;
  return data;
}

export async function getOrder(id: string): Promise<OnlineOrderDetail> {
  const { data, error } = await hoApi.GET('/api/v1/onlineordering/orders/{id}', { params: { path: { id } } });
  if (!data) throw error;
  return data;
}

export async function revealContact(id: string) {
  const { data, error } = await hoApi.POST('/api/v1/onlineordering/orders/{id}/reveal', { params: { path: { id } } });
  if (!data) throw error;
  return data;
}
